import React, { useState, useEffect } from 'react';
import { Volume2, Play, Save, RefreshCw } from 'lucide-react';
import { API_URL } from '../constants';

const VoiceSettingsTab = () => {
    const [settings, setSettings] = useState({
        stability: 0.5,
        similarity_boost: 0.75
    });
    const [currentSettings, setCurrentSettings] = useState(null);
    const [testing, setTesting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testAudio, setTestAudio] = useState(null);
    const [hasChanges, setHasChanges] = useState(false);

    useEffect(() => {
        fetchSettings();
    }, []);

    useEffect(() => {
        if (currentSettings) {
            const changed = 
                settings.stability !== currentSettings.default_stability ||
                settings.similarity_boost !== currentSettings.default_similarity_boost;
            setHasChanges(changed);
        }
    }, [settings, currentSettings]);

    const fetchSettings = async () => {
        try {
            const res = await fetch(`${API_URL}/api/tts/voice-settings`);
            const data = await res.json();
            setCurrentSettings(data);
            setSettings({
                stability: data.default_stability || 0.5,
                similarity_boost: data.default_similarity_boost || 0.75
            });
        } catch (err) {
            console.error('Error fetching voice settings:', err);
        }
    };

    const handleTestVoice = async () => {
        setTesting(true);
        setTestAudio(null);
        try {
            const res = await fetch(`${API_URL}/api/tts/generate`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    text: "Find a comfortable position. Let your eyes close gently. Take a deep breath in, and slowly release.",
                    stability: settings.stability,
                    similarity_boost: settings.similarity_boost
                })
            });
            const data = await res.json();
            if (data.audio) {
                const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
                setTestAudio(audio);
                audio.play();
            } else {
                alert('Error generating audio: ' + (data.detail || 'Unknown error'));
            }
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setTesting(false);
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/tts/voice-settings`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });
            if (res.ok) {
                alert('Voice settings saved!');
                fetchSettings();
            } else {
                alert('Error saving settings');
            }
        } catch (err) {
            alert('Error: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Voice Info */}
            <div className="bg-white/80 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                    <Volume2 className="w-6 h-6 text-[#7D8E95]" />
                    <h3 className="text-lg font-medium text-[#7D8E95]">ElevenLabs Voice Settings</h3>
                </div>
                
                {currentSettings && (
                    <div className="mb-6 p-4 bg-[#FBE0C3] rounded-lg">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="text-[#7D8E95]">Provider:</span>
                                <span className="ml-2 text-[#7D8E95] font-medium">ElevenLabs</span>
                            </div>
                            <div>
                                <span className="text-[#7D8E95]">Model:</span>
                                <span className="ml-2 text-[#7D8E95] font-medium">{currentSettings.model}</span>
                            </div>
                            <div>
                                <span className="text-[#7D8E95]">Voice ID:</span>
                                <span className="ml-2 text-[#7D8E95] font-mono text-xs">{currentSettings.voice_id}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* Stability Slider */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-[#7D8E95]">Stability</label>
                        <span className="text-sm text-[#7D8E95]">{(settings.stability * 100).toFixed(0)}%</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={settings.stability}
                        onChange={(e) => setSettings({ ...settings, stability: parseFloat(e.target.value) })}
                        className="w-full h-2 bg-[#FFBB98] rounded-lg appearance-none cursor-pointer accent-[#7D8E95]"
                    />
                    <div className="flex justify-between text-xs text-[#7D8E95] mt-1">
                        <span>More Variable</span>
                        <span>More Stable</span>
                    </div>
                    <p className="text-xs text-[#7D8E95] mt-2">
                        Higher stability makes the voice more consistent but less expressive. 
                        Lower stability adds more variation and emotion.
                    </p>
                </div>

                {/* Similarity Boost Slider */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-2">
                        <label className="text-sm font-medium text-[#7D8E95]">Similarity Boost</label>
                        <span className="text-sm text-[#7D8E95]">{(settings.similarity_boost * 100).toFixed(0)}%</span>
                    </div>
                    <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.05"
                        value={settings.similarity_boost}
                        onChange={(e) => setSettings({ ...settings, similarity_boost: parseFloat(e.target.value) })}
                        className="w-full h-2 bg-[#FFBB98] rounded-lg appearance-none cursor-pointer accent-[#7D8E95]"
                    />
                    <div className="flex justify-between text-xs text-[#7D8E95] mt-1">
                        <span>Less Similar</span>
                        <span>More Similar</span>
                    </div>
                    <p className="text-xs text-[#7D8E95] mt-2">
                        Higher values make the output sound more like your original voice sample.
                        Lower values allow more variation.
                    </p>
                </div>

                {/* Recommended Presets */}
                <div className="mb-6">
                    <label className="text-sm font-medium text-[#7D8E95] block mb-3">Quick Presets</label>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => setSettings({ stability: 0.3, similarity_boost: 0.8 })}
                            className="px-3 py-1.5 text-xs bg-[#FBE0C3] rounded-full hover:bg-[#FFBB98] transition-colors"
                        >
                            Expressive
                        </button>
                        <button
                            onClick={() => setSettings({ stability: 0.5, similarity_boost: 0.75 })}
                            className="px-3 py-1.5 text-xs bg-[#FBE0C3] rounded-full hover:bg-[#FFBB98] transition-colors"
                        >
                            Balanced (Default)
                        </button>
                        <button
                            onClick={() => setSettings({ stability: 0.7, similarity_boost: 0.9 })}
                            className="px-3 py-1.5 text-xs bg-[#FBE0C3] rounded-full hover:bg-[#FFBB98] transition-colors"
                        >
                            Consistent
                        </button>
                        <button
                            onClick={() => setSettings({ stability: 0.6, similarity_boost: 0.85 })}
                            className="px-3 py-1.5 text-xs bg-[#FBE0C3] rounded-full hover:bg-[#FFBB98] transition-colors"
                        >
                            Meditation (Calm)
                        </button>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center justify-between pt-4 border-t border-[#FFBB98]">
                    <button
                        onClick={handleTestVoice}
                        disabled={testing}
                        className="flex items-center gap-2 px-4 py-2 bg-[#7D8E95]/30 text-[#7D8E95] rounded-lg hover:bg-[#7D8E95]/50 transition-colors disabled:opacity-50"
                    >
                        {testing ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                            <Play className="w-4 h-4" />
                        )}
                        {testing ? 'Generating...' : 'Test Voice'}
                    </button>
                    
                    <div className="flex items-center gap-3">
                        {hasChanges && (
                            <span className="text-xs text-amber-600">Unsaved changes</span>
                        )}
                        <button
                            onClick={handleSaveSettings}
                            disabled={saving || !hasChanges}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                                hasChanges 
                                    ? 'bg-[#7D8E95] text-white hover:bg-[#8FB08A]' 
                                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            }`}
                        >
                            <Save className="w-4 h-4" />
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Tips */}
            <div className="bg-[#E3F2FD]/50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-[#7D8E95] mb-2">Tips for Meditation Voice</h4>
                <ul className="text-xs text-[#7D8E95] space-y-1">
                    <li>• For calm, soothing meditations, try Stability: 60-70%, Similarity: 80-90%</li>
                    <li>• For more emotional, expressive guidance, try Stability: 30-40%, Similarity: 70-80%</li>
                    <li>• Test different settings with the "Test Voice" button before saving</li>
                    <li>• Changes apply to all meditation tools (Body Scan, PMR, Guided Meditations)</li>
                </ul>
            </div>
        </div>
    );
};

export default VoiceSettingsTab;
